
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/brand.css';

import { AuthProvider, useAuth } from './auth/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GoogleMeetProvider } from './auth/GoogleMeetProvider';

import PrivateRoute from './components/PrivateRoute';
import LoginPage from './auth/LoginPage';
import SignupPage from './auth/SignupPage';
import LearnerDashboard from './pages/LearnerDashboard';
import LearnerAttendance from './pages/LearnerAttendance';
import FacilitatorDashboard from './pages/FacilitatorDashboard';
import LearnerDetailsPage from './pages/LearnerDetailsPage';
import ModulePage from './pages/ModulePage';
import LearnerProfile from './pages/LearnerProfile';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import UserDetailsPage from './pages/UserDetailsPage';
import IssueTracker from './pages/IssueTracker';
import AttendanceAnalytics from './pages/AttendanceAnalytics';
import ReportsAnalytics from './pages/ReportsAnalytics';
import ModuleContentPage from './pages/ModuleContentPage';
import PreviewPlaceholder from './components/PreviewPlaceholder';
import SystemAnalytics from './pages/SystemAnalytics';
import CohortManagement from './pages/CohortManagement';
import ProgramManagement from './pages/ProgramManagement';
import ProgressReports from './pages/ProgressReports';
import BEEExport from './pages/BEEExport';
import Assessments from './pages/Assessments';
import Messages from './pages/Messages';
import Notifications from './pages/Notifications';
import AttendanceReports from './pages/AttendanceReports';
import ComplianceReports from './pages/ComplianceReports';
import AdminProfile from './pages/AdminProfile';
import AdminSettings from './pages/AdminSettings';
import AdminDocumentUploadPage from './pages/AdminDocumentUploadPage';
import FacilitatorGradebook from './pages/FacilitatorGradebook';
import PlagiarismDashboard from './pages/PlagiarismDashboard';
import TakeAssessment from './pages/TakeAssessment';
import SubmitAssignment from './pages/SubmitAssignment';
import LearnerGrades from './pages/LearnerGrades';
import FacilitatorProfile from './pages/FacilitatorProfile';
import FacilitatorProgramManagement from './pages/FacilitatorProgramManagement';
import EmailComposer from './pages/EmailComposer';
import WhatsAppComposer from './pages/WhatsAppComposer';
import ResourceHub from './pages/ResourceHub';
import QADashboard from './pages/QADashboard';
import ExternalDashboard from './pages/ExternalDashboard';
import ExternalFacilitatorDashboard from './pages/ExternalFacilitatorDashboard';
import ExternalViewerDashboard from './pages/ExternalViewerDashboard';
import TakeALotSales from './pages/TakeALotSales';
import FacilitatorSalesDashboard from './pages/FacilitatorSalesDashboard';
import ModulesPage from './pages/ModulesPage';
import SupportPage from './pages/SupportPage';
import PageNotFound from './pages/PageNotFound';
import AppFooter from './components/AppFooter';
import TermsOfService from './pages/TermsOfService';
import DataCollectionPolicy from './pages/DataCollectionPolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { getDefaultRouteForRole, normalizeRole } from './services/roleManagementService';
import ErrorBoundary from './components/ErrorBoundary';

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { userProfile } = useAuth();
  
  if (!userProfile) {
    return <Navigate to="/login" replace />;
  }
  
  const normalizedRole = normalizeRole(userProfile.role);
  if (normalizedRole === 'external') {
    return <Navigate to="/external" replace />;
  }
  return <Navigate to={getDefaultRouteForRole(normalizedRole)} replace />;
};

// Combines PrivateRoute + ErrorBoundary so a crash in one page doesn't unmount the whole app
const ProtectedPage = ({ allowedRoles, requireRole, children }) => (
  <PrivateRoute allowedRoles={allowedRoles} requireRole={requireRole}>
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  </PrivateRoute>
);

// GoogleOAuthProvider throws if clientId is empty — skip it when not configured.
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const ConditionalGoogleOAuth = ({ children }) =>
  googleClientId
    ? <GoogleOAuthProvider clientId={googleClientId}>{children}</GoogleOAuthProvider>
    : <>{children}</>;

function App() {
  return (
    <ErrorBoundary>
    <ConditionalGoogleOAuth>
    <Router>
      <AuthProvider>
        <GoogleMeetProvider>
        <Routes>
          <Route path="/" element={<RoleBasedRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/data-collection-policy" element={<DataCollectionPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedPage allowedRoles={['learner']} requireRole={true}>
                <LearnerDashboard />
              </ProtectedPage>
            }
          />
          <Route
            path="/attendance"
            element={
              <ProtectedPage allowedRoles={['learner']} requireRole={true}>
                <LearnerAttendance />
              </ProtectedPage>
            }
          />
          <Route 
            path="/facilitator" 
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin']} requireRole={true}>
                <FacilitatorDashboard />
              </ProtectedPage>
            }
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedPage allowedRoles={['learner']} requireRole={true}>
                <LearnerProfile />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <AdminDashboard />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/users" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <UserManagement />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/user/:userId" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <UserDetailsPage />
              </ProtectedPage>
            }
          />
          <Route
            path="/admin/issues"
            element={
              <ProtectedPage allowedRoles={['admin', 'qa']} requireRole={true}>
                <IssueTracker />
              </ProtectedPage>
            }
          />
          <Route
            path="/admin/document-upload"
            element={
              <ProtectedPage allowedRoles={['admin', 'facilitator', 'assessor', 'qa']} requireRole={true}>
                <AdminDocumentUploadPage />
              </ProtectedPage>
            }
          />
          <Route
            path="/assessor"
            element={
              <ProtectedPage allowedRoles={['assessor']} requireRole={true}>
                <FacilitatorGradebook />
              </ProtectedPage>
            }
          />
          <Route
            path="/qa"
            element={
              <ProtectedPage allowedRoles={['qa', 'admin']} requireRole={true}>
                <QADashboard />
              </ProtectedPage>
            }
          />
          <Route
            path="/external"
            element={
              <ProtectedPage allowedRoles={['external']} requireRole={true}>
                <ExternalDashboard />
              </ProtectedPage>
            }
          />
          <Route
            path="/external-facilitator"
            element={
              <ProtectedPage allowedRoles={['external_facilitator_limited', 'external_facilitator']} requireRole={true}>
                <ExternalFacilitatorDashboard />
              </ProtectedPage>
            }
          />
          <Route
            path="/external-viewer"
            element={
              <ProtectedPage allowedRoles={['external_viewer_reporting', 'external_viewer']} requireRole={true}>
                <ExternalViewerDashboard />
              </ProtectedPage>
            }
          />
          <Route
            path="/admin/attendance"
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <AttendanceAnalytics />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/reports" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <ReportsAnalytics />
              </ProtectedPage>
            }
          />
          {/* Admin Placeholder Routes */}
          <Route 
            path="/admin/analytics" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <SystemAnalytics />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/cohorts" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <CohortManagement />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/programs" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <ProgramManagement />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/messages" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <Messages />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/notifications" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <Notifications />
              </ProtectedPage>
            }
          />
          <Route
            path="/admin/progress-reports"
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <ProgressReports />
              </ProtectedPage>
            }
          />
          {/* Reports route for QA */}
          <Route
            path="/reports"
            element={
              <ProtectedPage allowedRoles={['admin', 'facilitator', 'qa']} requireRole={true}>
                <ProgressReports />
              </ProtectedPage>
            }
          />
          <Route
            path="/admin/assessments" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <Assessments />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/reports/attendance" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <AttendanceReports />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/reports/compliance" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <ComplianceReports />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/reports/bee" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <BEEExport />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/profile" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <AdminProfile />
              </ProtectedPage>
            }
          />
          <Route 
            path="/admin/settings" 
            element={
              <ProtectedPage allowedRoles={['admin']} requireRole={true}>
                <AdminSettings />
              </ProtectedPage>
            }
          />
          
          {/* Facilitator Routes */}
          <Route 
            path="/facilitator/attendance" 
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin']} requireRole={true}>
                <AttendanceAnalytics />
              </ProtectedPage>
            }
          />
          <Route
            path="/facilitator/gradebook"
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin', 'assessor', 'external_facilitator_limited', 'external_facilitator']} requireRole={true}>
                <FacilitatorGradebook />
              </ProtectedPage>
            }
          />
          <Route
            path="/facilitator/plagiarism"
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin']} requireRole={true}>
                <PlagiarismDashboard />
              </ProtectedPage>
            }
          />
          <Route
            path="/facilitator/profile"
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin']} requireRole={true}>
                <FacilitatorProfile />
              </ProtectedPage>
            }
          />
          <Route
            path="/facilitator/programs"
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin']} requireRole={true}>
                <FacilitatorProgramManagement />
              </ProtectedPage>
            }
          />
          <Route
            path="/email-composer"
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin', 'qa', 'external_facilitator_limited', 'external_facilitator']} requireRole={true}>
                <EmailComposer />
              </ProtectedPage>
            }
          />
          <Route
            path="/whatsapp-composer"
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin']} requireRole={true}>
                <WhatsAppComposer />
              </ProtectedPage>
            }
          />
          {/* Take A Lot Sales Recording - Learner view */}
          <Route
            path="/take-a-lot-sales"
            element={
              <ProtectedPage allowedRoles={['learner']} requireRole={true}>
                <TakeALotSales />
              </ProtectedPage>
            }
          />
          {/* Take A Lot Sales Dashboard - Facilitator/Admin view */}
          <Route
            path="/facilitator/sales-dashboard"
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin', 'qa']} requireRole={true}>
                <FacilitatorSalesDashboard />
              </ProtectedPage>
            }
          />
          <Route
            path="/qa/sales-dashboard"
            element={
              <ProtectedPage allowedRoles={['qa', 'admin']} requireRole={true}>
                <FacilitatorSalesDashboard />
              </ProtectedPage>
            }
          />
          <Route
            path="/resources"
            element={
              <ProtectedPage allowedRoles={['learner', 'facilitator', 'admin', 'qa', 'external_facilitator_limited', 'external_facilitator']} requireRole={true}>
                <ResourceHub />
              </ProtectedPage>
            }
          />

          {/* Learner-specific routes */}
          <Route 
            path="/learner/:id" 
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin']} requireRole={true}>
                <LearnerDetailsPage />
              </ProtectedPage>
            }
          />
          <Route 
            path="/module/:moduleId" 
            element={
              <ProtectedPage allowedRoles={['facilitator', 'admin']} requireRole={true}>
                <ModulePage />
              </ProtectedPage>
            }
          />
          <Route 
            path="/learner/module/:moduleId" 
            element={
              <ProtectedPage allowedRoles={['learner']} requireRole={true}>
                <ModuleContentPage />
              </ProtectedPage>
            }
          />
          <Route 
            path="/learner/assessment/:assessmentId" 
            element={
              <ProtectedPage allowedRoles={['learner']} requireRole={true}>
                <TakeAssessment />
              </ProtectedPage>
            }
          />
          <Route 
            path="/learner/assignment/:assignmentId" 
            element={
              <ProtectedPage allowedRoles={['learner']} requireRole={true}>
                <SubmitAssignment />
              </ProtectedPage>
            }
          />
          <Route
            path="/modules"
            element={
              <ProtectedPage allowedRoles={['learner']} requireRole={true}>
                <ModulesPage />
              </ProtectedPage>
            }
          />
          <Route
            path="/learner/grades"
            element={
              <ProtectedPage allowedRoles={['learner']} requireRole={true}>
                <LearnerGrades />
              </ProtectedPage>
            }
          />

          {/* Support/Help Page - accessible to all authenticated users */}
          <Route
            path="/support"
            element={
              <ProtectedPage>
                <SupportPage />
              </ProtectedPage>
            }
          />

          {/* 404 Page Not Found - Catch all unmatched routes */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
        <AppFooter />
        </GoogleMeetProvider>
      </AuthProvider>
    </Router>
    </ConditionalGoogleOAuth>
    </ErrorBoundary>
  );
}

export default App;
